import Utils from "../shared/utils";
import { ErrorDialogComponent } from "../modalDialogs/errorDialog/errorDialog.component";
import { MatDialog } from "@angular/material";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { throwError, of } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { appConfig } from "../core-config";
import { env } from "../../../environments/environment";
import { YourAPPProductType } from "../shared/contentTypes";

/** vereinfachte kommunikation über https RESTfull */

/** Request dient für die Erstellung einer HTTP Anfrage */
export class Request {
  public baseURI?: string;
  public uri: string;
  public method: "GET" | "POST" | "PUT" | "DELETE";
  public headers?: {
    [header: string]: string;
  };
  public params?: {
    [header: string]: string;
  };
  public body?: any;
  public responseType?: "text" | "json" | "arraybuffer" | "blob";

  constructor(obj?: {
    baseURI?: string,
    uri: string,
    method: "GET" | "POST" | "PUT" | "DELETE",
    headers?: { [header: string]: string; };
    params?: { [param: string]: string; };
    body?: any;
    responseType?: "text" | "json" | "arraybuffer" | "blob";
  }) {
    if (obj === null) {
      return;
    }
    // übergebene parameter übernehmen
    this.baseURI = obj.baseURI;
    this.uri = obj.uri;
    this.method = obj.method;
    this.headers = obj.headers;
    this.params = obj.params;
    this.body = obj.body;
    this.responseType = obj.responseType;
  }
}

/** Representiert UappError.ErrorCode aus derm YourAppServer */
export const enum UappErrorCode {
  /** Es gibt kein UAPP Error */
  NONE = 0,
  /** Nicht definierter Fehler, meist durch Excaption verursacht */
  INTERNAL_ERROR = 100,
  /** Anfrage ist üngültig, siehe Message */
  INVALID_REQUEST = 101,
  /** Angefragter Controller, Action oder Inhalt wurden nicht gefunden */
  NOT_FOUND = 102,
  /** Validierungsfehler der Anfrage */
  VALIDATION_ERROR = 103,
  /** Verefezierung ist erforderlich */
  NEED_VERYFIED = 104,
  /** Zugang wird momentan gesperrt */
  BLOCKED = 105
}

/** Response dient als Container für die Antwort einer HTTP Anfrage */
export class Response {
  public ok: boolean = false;
  public ya_error_code: UappErrorCode = UappErrorCode.NONE;
  public code: number = 500;
  public codeText: string = "Internal Server Error";
  public headers: object = null;
  public body: any = null;

  constructor() { }
}

/** Interface um ein Request per http protocol abzusetzen */
export interface IHttpService {
  UrlencodedParams(params: object): any;
  Request(request: Request): Promise<Response>;
}

/** ATTENTION look to the node_http.service.ts by changing */

export class HttpService implements IHttpService {

  public constructor(
    private _httpClient: HttpClient,
    private _MatDialog: MatDialog,
  ) {
    const me = this;
    if (Utils.IsNull(me._httpClient)) {
      throw new Error("HttpService.Request httpClient param is null.");
    }
    if (Utils.IsNull(me._MatDialog)) {
      throw new Error("HttpService.Request _MatDialog param is null.");
    }
  }

  public UrlencodedParams(params: object): any {
    let httpParams = new HttpParams();
    if (params) {
      for (const param in params) {
        if (params.hasOwnProperty(param)) {
          httpParams = httpParams.set(param, params[param]);
        }
      }
    }
    return httpParams;
  }

  public Request(request: Request): Promise<Response> {
    const me = this;
    if (Utils.IsNull(request)) {
      throw new Error("HttpService.Request request param is null.");
    }

    let httpParams = new HttpParams();
    if (request.params) {
      for (const param in request.params) {
        if (request.params.hasOwnProperty(param)) {
          httpParams = httpParams.set(param, request.params[param]);
        }
      }
    }
    if (Utils.IsNull(request.headers)) {
      request.headers = {};
    }
    if (request.headers["YA-Version"] === undefined) {
      request.headers["YA-Version"] = appConfig.version
    }

    // Wenn Body keine HttpParams ist und "Content-Type" nicht explicit gesetzt, so ist es ein Objekt, übergeben wird es dann als JSON
    else if (!Utils.IsNull(request.body) && !(request.body instanceof HttpParams) && Utils.IsNull(request.headers["Content-Type"])) {
      request.headers["Content-Type"] = "application/json";
      request.body = JSON.stringify(request.body);
    }

    const httpOptions = {
      responseType: null,
      headers: new HttpHeaders(request.headers),
      params: httpParams,
      observe: "response" as "response"
      // withCredentials: true // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/withCredentials
    };

    // set responseType of options from request, because the type must be change
    if (request.responseType === "json") {
      httpOptions.responseType = "json" as "json";
    } else if (request.responseType === "text") {
      httpOptions.responseType = "text" as "text";
    } else if (request.responseType === "blob") {
      httpOptions.responseType = "blob" as "blob";
    } else if (request.responseType === "arraybuffer") {
      httpOptions.responseType = "arraybuffer" as "arraybuffer";
    } else {
      httpOptions.responseType = "json" as "json";
    }

    const uri = request.baseURI + request.uri;

    let obs = null;
    switch (request.method) {
      case "GET": {
        obs = me._httpClient.get<Response>(uri, httpOptions);
        break;
      }
      case "POST": {
        obs = me._httpClient.post<Response>(uri, request.body, httpOptions);
        break;
      }
      case "PUT": {
        obs = me._httpClient.put<Response>(uri, request.body, httpOptions);
        break;
      }
      case "DELETE": {
        obs = me._httpClient.delete<Response>(uri, httpOptions);
        break;
      }
      default: {
        throw new Error("Request.method '" + request.method + "' is unknown.");
      }
    }

    // rückgabe als Promise mit formatierung und error handling
    return obs.pipe(
      // map, um das Ergebniss als Response umzuwandeln
      map((result: { ok: boolean, status: number, statusText: string, headers: HttpHeaders, body: any }) => {
        const res = new Response();
        res.ok = result.ok;
        res.code = result.status;
        res.codeText = result.statusText;
        res.headers = {};
        result.headers.keys().forEach(headerKey => {
          res.headers[headerKey.toLowerCase()] = result.headers.get(headerKey);
        });
        res.body = result.body;
        return res;
      }),
      // catchError bei einer erfolgreicher Anfrage, diese als Response zurückgeben
      // sonst einen wirklichen Fehler auslösen
      catchError((result, main_obs) => {
        // hat der Server geantwortet mit Code? (ohne Code 2xx wird es leider als Fehler behandelt)
        if (result.status !== 0) { // code = httpCode
          const res = new Response();
          res.ok = false; // is a error
          res.code = result.status;
          res.codeText = result.statusText;
          // todo it must checkt with YourAppError
          if (!Utils.IsNull(result.error) && !Utils.IsNull(result.error.code)) {
            res.ya_error_code = result.error.code;
            res.code = result.error.code;
            res.codeText = result.error.status;
          } else {
            res.ya_error_code = UappErrorCode.INTERNAL_ERROR;
          }
          // check health on INTERNAL_ERROR
          if (res.ya_error_code == UappErrorCode.INTERNAL_ERROR) {
            try {
              if (request.baseURI.toLowerCase().endsWith(".myhypersoftapp.de/")) {
                const healthUri = request.baseURI + "api/health/check";
                const healthHttpOptions = {
                  responseType: null,
                  headers: new HttpHeaders(request.headers),
                  observe: "response" as "response"
                };
                me._httpClient.get<Response>(healthUri, healthHttpOptions).toPromise().then(healthRes => {
                  // health is okay, error is something else 
                }).catch(healthRes => {
                  if (healthRes && healthRes.error && healthRes.error.portalConnectorHealth) {
                    if (!healthRes.error.portalConnectorHealth.liveStatus) {
                      if (me._MatDialog.openDialogs.length == 0 && env.producttype != YourAPPProductType.sot) {
                        me._MatDialog.open(ErrorDialogComponent, {
                          data: {
                            title: "phErrorMessage_Title"
                            , description: "phErrorMessage_Text"
                          },
                          panelClass: "error-dialog-styles"
                        });
                      }
                    }
                  }
                });
              }
              // else no health check needed
            } catch (error) {
              console.error("health check has error", error);
            }
          }

          res.headers = {};
          if (result.headers) {
            result.headers.keys().forEach(headerKey => {
              res.headers[headerKey.toLowerCase()] = result.headers.get(headerKey);
            });
          }
          res.body = result.error; // error is the body of result
          return of(res); // als erfolgreiche abfrage zurückgeben
        } else {
          // YourAppServer nicht erreichbar
          if (request.baseURI.toLowerCase().endsWith(".myhypersoftapp.de/")) {
            if (me._MatDialog.openDialogs.length == 0 && env.producttype != YourAPPProductType.sot) {
              // TODO bei InHouse kann es passieren, dass der erste Call fehlschlägt, wenn internet wieder verfügbar
              const ignore_error = uri.endsWith(".myhypersoftapp.de/api/socket/GetToken") || uri.endsWith(".myhypersoftapp.de/api/socket/GetURLs");
              if (!ignore_error) {
                me._MatDialog.open(ErrorDialogComponent, {
                  data: {
                    title: "phErrorMessage_Title"
                    , description: "phErrorMessage_Text"
                  },
                  panelClass: "error-dialog-styles"
                });
              }
            }
          }
          return throwError(result); // mit fehler abbruch
        }
      })
    ).toPromise(); // rückgabe als Promise
  }
}
